import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { onAuthStateChanged, User } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import {
  LOCAL_E2E_FEATURES,
  createLocalE2EUser,
  isLocalE2EAuthEnabled,
} from "../../localE2eAuth";

/**
 * RequirePremiumFeature Component
 * 
 * Feature gate component that checks if user has access to premium features.
 * 
 * This component:
 * 1. Shows a loading state while auth is initializing
 * 2. Redirects to /login if not authenticated
 * 3. Checks for featureKey in user.features array
 * 4. Redirects to /cfo-insights/upgrade if feature is missing
 * 
 * Expected user object structure (from Firestore or custom claims):
 * {
 *   features: string[] // e.g. ["cfo_insights", "bondready"]
 * }
 * 
 * Features can come from:
 * - Firestore: users/{userId}/features array
 * - Firebase Custom Claims: user.customClaims.features array
 */
type RequirePremiumFeatureProps = {
  featureKey: string; // e.g. "cfo_insights"
  children: React.ReactNode;
  /**
   * Optional Firestore defaultRoute allow-list.
   * Used for surfaces whose established entitlement is routing state
   * (e.g. LENDER → /dashboard) rather than a premium feature flag alone.
   */
  allowDefaultRoutes?: string[];
};

type UserWithFeatures = {
  features?: string[];
  defaultRoute?: string;
  // Allow for custom claims structure as well
  customClaims?: {
    features?: string[];
  };
};

/**
 * Hook to get current Firebase auth user and their features
 * 
 * Checks both Firestore user document and custom claims for features array.
 * Uses the existing Firebase initialization from firebase.js
 */
function useAuth() {
  const [user, setUser] = useState<UserWithFeatures | null>(null);
  const [loading, setLoading] = useState(true);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);

  const location = useLocation();

  useEffect(() => {
    if (isLocalE2EAuthEnabled()) {
      const params = new URLSearchParams(location.search);
      const nextUid = params.get("localE2eUid");
      const localUser = createLocalE2EUser(nextUid || undefined) as unknown as User;
      setFirebaseUser(localUser);
      setUser({
        features: LOCAL_E2E_FEATURES,
        customClaims: { features: LOCAL_E2E_FEATURES },
      });
      setLoading(false);
      return undefined;
    }

    // Listen to auth state changes using the initialized auth instance
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setFirebaseUser(null);
        setUser(null);
        setLoading(false);
        return;
      }

      setFirebaseUser(currentUser);

      try {
        // Get custom claims from ID token
        const idTokenResult = await currentUser.getIdTokenResult();
        const claims = idTokenResult.claims as { features?: string[] } | undefined;
        
        // Check Firestore for user features
        const userDocRef = doc(db, "users", currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUser({
            features: userData.features || [],
            defaultRoute:
              typeof userData.defaultRoute === "string"
                ? userData.defaultRoute
                : undefined,
            customClaims: claims,
          });
        } else {
          // If no Firestore doc, check custom claims only
          setUser({
            features: [],
            customClaims: claims,
          });
        }
      } catch (error) {
        console.error("Error fetching user features:", error);
        setUser({
          features: [],
        });
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [location.search]);

  // Combine features from both sources (Firestore takes precedence)
  const allFeatures = [
    ...(user?.features || []),
    ...(user?.customClaims?.features || []),
  ];

  return {
    user: user ? { ...user, features: allFeatures } : null,
    loading,
    firebaseUser,
  };
}

export function RequirePremiumFeature({
  featureKey,
  children,
  allowDefaultRoutes = [],
}: RequirePremiumFeatureProps) {
  const { user, loading, firebaseUser } = useAuth();
  const location = useLocation();

  // Show loading state while auth is initializing
  if (loading) {
    return (
      <div className="flex h-full min-h-screen items-center justify-center bg-slate-950 text-slate-200">
        <div className="text-center">
          <div className="mb-2 text-sm text-slate-400">Checking access…</div>
          <div className="h-1 w-32 animate-pulse rounded bg-slate-800"></div>
        </div>
      </div>
    );
  }

  // Not logged in → redirect to login
  if (!firebaseUser || !user) {
    console.log("RequirePremiumFeature: No user, redirecting to login");
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const features: string[] = user.features ?? [];
  const defaultRoute = user.defaultRoute;
  const hasFeature = features.includes(featureKey);
  const hasAllowedDefaultRoute =
    typeof defaultRoute === "string" &&
    allowDefaultRoutes.includes(defaultRoute);

  console.log("RequirePremiumFeature: User ID:", firebaseUser.uid);
  console.log(
    "RequirePremiumFeature: User features:",
    features,
    "Looking for:",
    featureKey,
    "defaultRoute:",
    defaultRoute
  );

  // Feature / established defaultRoute entitlement missing → upgrade page
  if (!hasFeature && !hasAllowedDefaultRoute) {
    console.log("RequirePremiumFeature: Feature not found, redirecting to upgrade");
    console.log("💡 To enable this feature, add 'features' array to Firestore:");
    console.log(`   Collection: users`);
    console.log(`   Document ID: ${firebaseUser.uid}`);
    console.log(`   Field: features = ["${featureKey}"]`);
    return <Navigate to="/cfo-insights/upgrade" replace />;
  }

  // User has access → render children
  return <>{children}</>;
}
